// ── ADMIN: NOTIFICATIONS (email templates) ──────────────────────────────
// esc (shared classic script) stays a bare global — not migrated yet.
// Wired via VIEW_INIT (phase 2 of the onclick → addEventListener migration)
// instead of window/onclick. Every re-render here goes through refresh() →
// route(), so VIEW_INIT is sufficient — no direct-DOM-rewrite bypass like
// admin-billing.js/catalog-shared.js needed elsewhere in this migration.
import { DB } from '../core/state.js';
import { apiFetch } from '../core/api.js';
import { toast, toastError } from '../core/ui.js';
import { VIEWS, VIEW_INIT, refresh, sectionTabs } from '../core/routing.js';

let expandedTemplate = null;

VIEWS['a-notifications'] = () => {
  const templates = DB.settings.emailTemplates;
  // Derived from the templates themselves rather than a fixed list. The list
  // used to be hard-coded as Billing/Account/Registration, which silently hid
  // every template added since -- Summer Workshops, Coaching, Merch Store and
  // Classes were all being sent to families with no way to read or reword them
  // here. Order follows SettingsRoutes' own authoring order.
  const groups = templates.reduce((acc, t) => acc.includes(t.group) ? acc : [...acc, t.group], []);
  return `
    <div class="page-head">
      <h2 class="serif">Notifications</h2>
    </div>
    ${sectionTabs('a-settings')}
    ${groups.map(g => `
      <div style="margin-bottom:26px">
        <div style="font-size:11px;letter-spacing:0.14em;text-transform:uppercase;color:var(--gold);margin-bottom:10px">${g}</div>
        ${templates.filter(t => t.group === g).map(t => {
          const open = expandedTemplate === t.id;
          return `
          <div class="card" style="margin-bottom:10px;padding:0;overflow:hidden">
            <div style="display:flex;align-items:center;justify-content:space-between;gap:12px;padding:14px 18px;cursor:pointer" data-action="toggle-template" data-template="${t.id}">
              <div style="display:flex;align-items:center;gap:12px;min-width:0">
                <span style="font-size:14px;color:var(--cream)">${esc(t.name)}</span>
                ${!t.active ? '<span class="pill pill-off" style="font-size:10px">off</span>' : ''}
              </div>
              <div style="display:flex;align-items:center;gap:14px;flex-shrink:0">
                <label style="display:flex;align-items:center;gap:7px;cursor:pointer;font-size:12px;color:var(--muted)" data-action="stop-propagation">
                  <input type="checkbox" style="width:auto" ${t.active ? 'checked' : ''} data-action="toggle-template-active" data-template="${t.id}">
                  Active
                </label>
                <span style="color:var(--gold);font-size:13px;transform:rotate(${open ? '90deg' : '0deg'});transition:transform 0.15s">›</span>
              </div>
            </div>
            ${open ? `
            <div style="padding:0 18px 18px;border-top:1px solid var(--border-soft)">
              <div class="field" style="margin-top:16px"><label>Subject</label>
                <input type="text" id="tpl-subj-${t.id}" value="${esc(t.subject)}"></div>
              <div class="field" style="margin-top:12px"><label>Body</label>
                <textarea id="tpl-body-${t.id}" rows="10" style="background:var(--bg-input);border:1px solid var(--border-soft);border-radius:7px;padding:11px 13px;color:var(--cream);font-family:'Source Sans 3',sans-serif;font-size:14px;font-weight:300;line-height:1.6;outline:none;width:100%;resize:vertical" onfocus="this.style.borderColor='var(--gold)'" onblur="this.style.borderColor='var(--border-soft)'">${esc(t.body)}</textarea></div>
              <div style="margin-top:10px;font-size:12px;color:var(--muted)">
                Available placeholders: ${t.tokens.map(tk => `<code style="color:var(--gold-light);background:rgba(201,168,76,0.08);padding:2px 6px;border-radius:4px;font-size:11px;margin:0 2px;cursor:pointer" data-action="insert-token" data-template="${t.id}" data-token="{{${tk}}}">{{${tk}}}</code>`).join('')}
              </div>
              <div style="display:flex;gap:10px;margin-top:16px">
                <button class="btn-ghost" style="width:auto" data-action="save-template" data-template="${t.id}">Save</button>
                <button class="btn-ghost" style="width:auto;color:var(--muted)" data-action="toggle-template" data-template="${t.id}">Cancel</button>
              </div>
            </div>` : ''}
          </div>`;
        }).join('')}
      </div>`).join('')}`;
};

VIEW_INIT['a-notifications'] = () => {
  document.querySelectorAll('[data-action="toggle-template"]').forEach(el => {
    el.addEventListener('click', () => toggleTemplate(el.dataset.template));
  });
  document.querySelectorAll('[data-action="stop-propagation"]').forEach(el => {
    el.addEventListener('click', e => e.stopPropagation());
  });
  document.querySelectorAll('[data-action="toggle-template-active"]').forEach(cb => {
    cb.addEventListener('change', () => toggleTemplateActive(cb.dataset.template, cb.checked));
  });
  document.querySelectorAll('[data-action="insert-token"]').forEach(el => {
    el.addEventListener('click', () => insertToken(el.dataset.template, el.dataset.token));
  });
  document.querySelectorAll('[data-action="save-template"]').forEach(btn => {
    btn.addEventListener('click', () => saveTemplate(btn.dataset.template));
  });
};

function toggleTemplate(id) {
  expandedTemplate = expandedTemplate === id ? null : id;
  refresh();
}
async function toggleTemplateActive(id, val) {
  try {
    await apiFetch('/settings/email-templates/' + id + '/toggle', { method: 'PUT', body: JSON.stringify({ active: val }) });
    const t = DB.settings.emailTemplates.find(x => x.id === id);
    if (t) t.active = val;
    refresh(); toast(val ? 'Notification turned on' : 'Notification turned off');
  } catch (e) { toastError(e.message); }
}
function insertToken(id, token) {
  const ta = document.getElementById('tpl-body-' + id);
  if (!ta) return;
  const start = ta.selectionStart, end = ta.selectionEnd;
  ta.value = ta.value.slice(0, start) + token + ta.value.slice(end);
  ta.focus();
  ta.selectionStart = ta.selectionEnd = start + token.length;
}
async function saveTemplate(id) {
  const t = DB.settings.emailTemplates.find(x => x.id === id);
  if (!t) return;
  const subj = document.getElementById('tpl-subj-' + id);
  const body = document.getElementById('tpl-body-' + id);
  try {
    await apiFetch('/settings/email-templates/' + id, {
      method: 'PUT', body: JSON.stringify({ subject: subj ? subj.value : t.subject, body: body ? body.value : t.body })
    });
    if (subj) t.subject = subj.value;
    if (body) t.body = body.value;
    expandedTemplate = null;
    refresh(); toast(t.name + ' saved');
  } catch (e) { toastError(e.message); }
}
